<template>
  <div class="pt-3 px-3 overview-container">
    <!-- Header: welcome text & date selector aligned just like analytics section -->
    <div class="overview-header-container">
      <h3 class="welcome-message mb-0">{{ greeting }}, {{ userName }}</h3>

      <v-date-picker
        class="daterange-picker"
        popover-direction="bottom"
        popover-align="right"
        mode="range"
        show-caps
        :drag-attribute="attribute"
        :select-attribute="attribute"
        :value="getDateRange"
        :input-props="{ style: dateInputStyle }"
        @drag="onDragDates"
        @input="onInputDates"
      >
        <div slot="popover" class="popover-content">
          <p>{{ popStartDate }}</p>
          <p>{{ popEndDate }}</p>
          <p class="days-nights">
            <span class="days">
              <svg-icon name="sun" class="vc-sun-o"> </svg-icon>
              {{ popDays }}
            </span>
            <span class="nights">
              <svg-icon name="moon" class="vc-moon-o"> </svg-icon>
              {{ popDays }}
            </span>
          </p>
        </div>
      </v-date-picker>
    </div>
    <!-- GROUPED METRIC CARDS -->
    <b-row class="grouped-metric-row">
      <!-- Guests / Bookings Group -->
      <b-col md="12" lg="4" class="mb-3">
        <div class="card group-card">
          <div class="card-header text-center">Guests &amp; Bookings</div>
          <div class="card-body metric-grid">
            <div
              v-for="metric in group1Metrics"
              :key="metric.id"
              class="metric-item text-center"
            >
              <div
                :class="[
                  'metric-icon',
                  metric.type === 'circle' ? '' : metric.type,
                ]"
              >
                <percentage-circle
                  v-if="metric.type === 'circle'"
                  :value="metric.value"
                  :text="formatPercent(metric.value, 2)"
                />
                <fi-dollar v-else-if="metric.type === 'icon-dollar'" />
                <plane-arrival v-else-if="metric.type === 'icon-arrival'" />
                <plane-departure v-else-if="metric.type === 'icon-departure'" />
                <fi-booking v-else-if="metric.type === 'icon-booking'" />
                <fi-guest v-else-if="metric.type === 'icon-guest'" />
              </div>
              <div class="metric-value mt-1">
                <h5 class="mb-0">
                  {{
                    metric.format === "normal"
                      ? metric.value
                      : formatMoney(metric.value, metric.decimals, true, false)
                  }}
                </h5>
                <small class="text-muted">{{ metric.label }}</small>
              </div>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Occupancy / ADR / RevPar Group -->
      <b-col md="12" lg="4" class="mb-3">
        <div class="card group-card">
          <div class="card-header text-center">Performance</div>
          <div class="card-body metric-grid">
            <div
              v-for="metric in group2Metrics"
              :key="metric.id"
              class="metric-item text-center"
            >
              <div :class="['metric-icon', metric.type]">
                <percentage-circle
                  v-if="metric.type === 'circle'"
                  :value="metric.value"
                  :text="formatPercent(metric.value, 2)"
                />
                <fi-dollar v-else-if="metric.type === 'icon-dollar'" />
                <fi-percent v-else-if="metric.type === 'icon-percent'" />
              </div>
              <div class="metric-value mt-1">
                <h5 class="mb-0">
                  {{
                    metric.type === "circle"
                      ? formatPercent(metric.value, 2)
                      : formatMoney(metric.value, metric.decimals, true, false)
                  }}
                </h5>
                <small class="text-muted">{{ metric.label }}</small>
              </div>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Revenue Group -->
      <b-col md="12" lg="4" class="mb-3">
        <div class="card group-card">
          <div class="card-header text-center">Revenue</div>
          <div class="card-body metric-grid">
            <div
              v-for="metric in group3Metrics"
              :key="metric.id"
              class="metric-item text-center"
            >
              <div class="metric-icon icon-dollar">
                <fi-dollar />
              </div>
              <div class="metric-value mt-1">
                <h5 class="mb-0">
                  {{ formatMoney(metric.value, 2, true, false) }}
                </h5>
                <small class="text-muted">{{ metric.label }}</small>
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row class="pt-3">
      <b-col md="12" lg="7" class="mb-3">
        <div class="overview-chart-wrapper">
          <div class="chart-title">
            <h4>
              {{
                chart_daily
                  ? "Daily Property Revenue"
                  : "Monthly Property Revenue"
              }}
            </h4>
            <a v-if="chart_daily" class="btn-back" @click="chart_daily = false">
              Monthly view &gt;
            </a>
            <span v-else class="btn-back cursor-normal">
              * Click on a data point for daily view
            </span>
          </div>
          <line-chart
            v-if="chartDataReady"
            :chart-data="!chart_daily ? chartDataMonthly : chartDataDaily"
            :options="lineChartOptions"
            @chart:render="setupLineChartEvents"
          />
        </div>
      </b-col>
      <b-col md="12" lg="5" class="mb-3" v-if="hasPOSAccess">
        <div class="overview-chart-wrapper">
          <div class="chart-title">
            <h4>Sales Revenue by Category</h4>
          </div>
          <doughnut-chart
            v-if="chart_category_data && chart_category_data.length > 0"
            :chart-data="chartDataCategory"
          />
          <div v-else class="chart-placeholder">
            <p>No category data available</p>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import AnalyticsMixin from "./analyticsViews/mixin";
import { BRow, BCol } from "bootstrap-vue";
import PercentageCircle from "@/components/PercentageCircle.vue";
import LineChart from "@/components/charts/LineChart.vue";
import DoughnutChart from "@/components/charts/DoughnutChart.vue";
import planeArrivalIcon from "@/assets/icons/plane-arrival.png";
import planeDepartureIcon from "@/assets/icons/plane-departure.png";
import { mapGetters } from "vuex";

export default {
  name: "analytics-overview",

  components: {
    FiDollar: {
      computed: {
        symbol() {
          return (
            (this.$store &&
              this.$store.getters &&
              this.$store.getters.getVendor &&
              this.$store.getters.getVendor.local_currency) ||
            "$"
          );
        },
      },
      render(h) {
        return h(
          "div",
          {
            class: "svg-icon-wrapper dollar-icon",
            style: {
              fontSize: "22px",
              fontWeight: "bold",
              backgroundColor: "transparent",
            },
          },
          this.symbol
        );
      },
    },
    FiPercent: {
      functional: true,
      render(h) {
        return h(
          "div",
          {
            class: "svg-icon-wrapper percent-icon",
            style: {
              fontSize: "22px",
              fontWeight: "bold",
              backgroundColor: "transparent",
            },
          },
          "%"
        );
      },
    },
    PercentageCircle,
    PlaneArrival: {
      functional: true,
      render(h) {
        return h(
          "div",
          {
            class: "svg-icon-wrapper plane-icon",
          },
          [
            h("img", {
              attrs: {
                src: planeArrivalIcon,
                alt: "Arrivals",
              },
              style: {
                width: "30px",
                height: "30px",
              },
            }),
          ]
        );
      },
    },
    PlaneDeparture: {
      functional: true,
      render(h) {
        return h(
          "div",
          {
            class: "svg-icon-wrapper plane-icon",
          },
          [
            h("img", {
              attrs: {
                src: planeDepartureIcon,
                alt: "Departures",
              },
              style: {
                width: "30px",
                height: "30px",
              },
            }),
          ]
        );
      },
    },
    FiBooking: {
      functional: true,
      render(h) {
        return h(
          "div",
          {
            class: "svg-icon-wrapper booking-icon",
          },
          [h("i", { class: "fi-calendar" })]
        );
      },
    },
    FiGuest: {
      functional: true,
      render(h) {
        return h(
          "div",
          {
            class: "svg-icon-wrapper guest-icon",
          },
          [h("i", { class: "fi-torsos" })]
        );
      },
    },
    LineChart,
    DoughnutChart,
    BRow,
    BCol,
    SvgIcon: () => import("@/components/widgets/SvgIcon"),
  },

  mixins: [AnalyticsMixin],

  data() {
    return {
      // Local date range for Overview – decoupled from analytics date selector
      dateRange: {
        start: new Date(new Date().toISOString().split("T")[0]),
        end: new Date(new Date().toISOString().split("T")[0]),
      },
      dateInputStyle:
        "width:220px; text-align:center; border-radius:50px; border:1px solid gray; height:34px;",
      popStartDate: "",
      popEndDate: "",
      popDays: "",
      card_overviews: [
        {
          id: "occupancy_rate",
          label: "Occupancy",
          color: "bg-occupancy",
          type: "icon-percent",
          value: 0,
          decimals: 2,
          format: "normal",
        },
        {
          id: "adr",
          label: "Average Daily Rate",
          color: "bg-adr",
          type: "icon-dollar",
          value: 0,
          decimals: 2,
          format: "normal",
        },
        {
          id: "rev_par",
          label: "RevPar",
          color: "bg-rev-par",
          type: "icon-dollar",
          value: 0,
          decimals: 2,
          format: "normal",
        },
        {
          id: "departures",
          label: "Departures",
          color: "bg-departures",
          type: "icon-departure",
          value: 0,
          decimals: 0,
          format: "normal",
        },
        {
          id: "arrivals",
          label: "Arrivals",
          color: "bg-arrivals",
          type: "icon-arrival",
          value: 0,
          decimals: 0,
          format: "normal",
        },
        {
          id: "total_no_bookings",
          label: "Total Bookings",
          color: "bg-bookings",
          type: "icon-booking",
          value: 0,
          decimals: 0,
          format: "normal",
        },
        {
          id: "total_no_guests",
          label: "Total Guests",
          color: "bg-guests",
          type: "icon-guest",
          value: 0,
          decimals: 0,
          format: "normal",
        },
      ],

      card_revenues: [
        {
          id: "property_revenue",
          label: "Property Revenue",
          color: "",
          value: 0,
        },
        {
          id: "hotel_revenue",
          label: "Hotel Revenue",
          color: "",
          value: 0,
        },
        {
          id: "sales_revenue",
          label: "Sales Revenue",
          color: "",
          value: 0,
        },
      ],

      chart_mtd_data: [],
      chart_dtd_data: [],
      chart_category_data: [],
      chart_daily: false,
      month: 0,
      month_names: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
    };
  },

  computed: {
    ...mapGetters(["getUser"]),
    ...mapGetters("subscription", ["hasPOSAccess"]),
    chartDataReady() {
      return this.chart_mtd_data.length > 0;
    },

    chartDataMonthly() {
      const labels = this.chart_mtd_data.map(
        (_, index) => this.month_names[index]
      );
      const unitData = this.chart_mtd_data.map(
        (item) => item.unit_revenue || 0
      );
      const itemData = this.chart_mtd_data.map(
        (item) => item.item_revenue || 0
      );

      return {
        labels: labels,
        datasets: [
          {
            label: "Accommodations",
            backgroundColor: "rgba(0, 123, 255, 0.2)",
            borderColor: "rgba(0, 123, 255, 1)",
            borderWidth: 2,
            pointBackgroundColor: "rgba(0, 123, 255, 1)",
            pointBorderColor: "#fff",
            pointRadius: 5,
            pointHoverRadius: 7,
            data: unitData,
          },
          {
            label: "Inventory Items",
            backgroundColor: "rgba(234, 197, 78, 0.2)",
            borderColor: "rgba(234, 197, 78, 1)",
            borderWidth: 2,
            pointBackgroundColor: "rgba(234, 197, 78, 1)",
            pointBorderColor: "#fff",
            pointRadius: 5,
            pointHoverRadius: 7,
            data: itemData,
          },
        ],
      };
    },

    chartDataDaily() {
      if (!this.chart_dtd_data.length) return { labels: [], datasets: [] };

      const labels = this.chart_dtd_data.map((item, index) => index + 1);
      const unitData = this.chart_dtd_data.map(
        (item) => (item.revenue && item.revenue.unit_revenue) || 0
      );
      const itemData = this.chart_dtd_data.map(
        (item) => (item.revenue && item.revenue.item_revenue) || 0
      );

      return {
        labels: labels,
        datasets: [
          {
            label: "Accommodations",
            backgroundColor: "rgba(0, 123, 255, 0.2)",
            borderColor: "rgba(0, 123, 255, 1)",
            borderWidth: 2,
            pointBackgroundColor: "rgba(0, 123, 255, 1)",
            pointBorderColor: "#fff",
            pointRadius: 5,
            pointHoverRadius: 7,
            data: unitData,
          },
          {
            label: "Inventory Items",
            backgroundColor: "rgba(234, 197, 78, 0.2)",
            borderColor: "rgba(234, 197, 78, 1)",
            borderWidth: 2,
            pointBackgroundColor: "rgba(234, 197, 78, 1)",
            pointBorderColor: "#fff",
            pointRadius: 5,
            pointHoverRadius: 7,
            data: itemData,
          },
        ],
      };
    },

    chartDataCategory() {
      // Default empty structure
      const emptyData = {
        labels: ["No Data Available"],
        datasets: [
          {
            data: [1],
            backgroundColor: ["#f0f0f0"],
            borderWidth: 0,
          },
        ],
      };

      // Take the top 5 categories with valid structure
      const categoryData = this.chart_category_data
        .filter(
          (item) => item && item.data && item.data.name && item.revenue != null
        )
        .slice(0, 5);

      const totalRevenue = categoryData.reduce(
        (sum, c) => sum + Number(c.revenue || 0),
        0
      );

      if (categoryData.length === 0 || totalRevenue === 0) {
        return emptyData;
      }

      const labels = categoryData.map((item) => item.data.name);
      const data = categoryData.map((item) => Number(item.revenue || 0));

      // Fixed colors for consistent appearance
      const backgroundColors = [
        "rgba(54, 162, 235, 0.8)", // Blue
        "rgba(255, 99, 132, 0.8)", // Pink
        "rgba(255, 206, 86, 0.8)", // Yellow
        "rgba(75, 192, 192, 0.8)", // Teal
        "rgba(153, 102, 255, 0.8)", // Purple
      ];

      return {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: backgroundColors.slice(0, data.length),
            borderWidth: 1,
            borderColor: "#ffffff",
          },
        ],
      };
    },

    lineChartOptions() {
      const year = this.today().getFullYear();
      return {
        responsive: true,
        maintainAspectRatio: false,
        title: {
          display: false,
        },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                callback: (value) => {
                  return "$" + value.toLocaleString();
                },
              },
            },
          ],
          xAxes: [
            {
              ticks: {
                callback: (value, index) => {
                  if (this.chart_daily) {
                    return value;
                  } else {
                    return this.month_names[index];
                  }
                },
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            title: (tooltipItems) => {
              const index = tooltipItems[0].index;
              if (this.chart_daily) {
                return `${tooltipItems[0].xLabel} ${
                  this.month_names[this.month - 1]
                } ${year}`;
              } else {
                return `${this.month_names[index]} ${year}`;
              }
            },
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const value = dataset.data[tooltipItem.index];
              return `${dataset.label}: ${this.formatMoney(
                value,
                2,
                true,
                false
              )}`;
            },
          },
        },
      };
    },

    // groups for new cards
    group1Metrics() {
      const ids = [
        "total_no_guests",
        "arrivals",
        "departures",
        "total_no_bookings",
      ];
      return this.card_overviews.filter((m) => ids.includes(m.id));
    },
    group2Metrics() {
      const ids = ["occupancy_rate", "adr", "rev_par"];
      return this.card_overviews.filter((m) => ids.includes(m.id));
    },
    group3Metrics() {
      // Hide Sales Revenue metric unless POS access is enabled
      if (this.hasPOSAccess) {
        return this.card_revenues;
      }
      return this.card_revenues.filter((m) => m.id !== "sales_revenue");
    },

    // Styling attribute for v-date-picker selection & drag ranges
    attribute() {
      let attribute_ = {
        popover: {
          slot: "popover",
        },
      };

      if (this.popDays === 1) {
        attribute_.contentStyle = {
          backgroundColor: "rgb(102, 179, 204)",
          color: "white",
          fontWeight: "400",
          borderRadius: "50%",
        };
      } else {
        attribute_.contentStyle = {
          backgroundColor: "transparent",
          color: "inherit",
          fontWeight: "400",
          borderRadius: "3px",
        };
      }

      return attribute_;
    },

    // Override mixin getter – use component-local dateRange
    getDateRange() {
      return this.dateRange;
    },

    greeting() {
      const hour = new Date().getHours();
      if (hour < 12) return "Good Morning";
      if (hour < 18) return "Good Afternoon";
      return "Good Evening";
    },

    todayDate() {
      return this.$moment().format("ddd, D MMM YYYY");
    },

    vendorDisplay() {
      return this.getVendor && this.getVendor.name ? this.getVendor.name : "";
    },

    userName() {
      const user = this.getUser || {};
      // Prefer first_name if exists, else fallback to email or blank
      if (user.first_name) {
        return `${user.first_name}`.trim();
      }
      return "";
    },
  },

  mounted() {
    this.setPopoverInfo(this.getDateRange);
    this.loadData();
  },

  methods: {
    setupLineChartEvents(chartInstance) {
      // Only set up click handlers for monthly chart
      if (!this.chart_daily && chartInstance) {
        const canvas = chartInstance.chart.canvas;
        const year = this.today().getFullYear();

        canvas.onclick = (evt) => {
          const activePoints = chartInstance.getElementsAtEvent(evt);
          if (activePoints[0]) {
            const clickedIndex = activePoints[0]._index;
            const month = clickedIndex + 1; // Months are 1-indexed in our data
            this.loadDailyRevenue(year, month);
          }
        };
      }
    },

    loadData() {
      this.loading = true;

      Promise.all([this.fetchData("overview"), this.fetchData("summary")])
        .then(([overviewRes, summaryRes]) => {
          const overviewSummary = overviewRes.data.summary || {};
          const summaryItem =
            summaryRes.data && summaryRes.data.length > 0
              ? summaryRes.data[0]
              : {};
          const mergedSummary = { ...overviewSummary, ...summaryItem };

          this.card_overviews = this.card_overviews.map((item) => {
            item.value = mergedSummary[item.id] || 0;
            return item;
          });

          this.card_revenues = this.card_revenues.map((item) => {
            item.value = mergedSummary[item.id] || 0;
            return item;
          });

          this.chart_mtd_data = overviewRes.data.mtd_revenue || [];
          this.chart_category_data = overviewRes.data.tag_usage || [];
        })
        .catch((err) => {
          this.errorNotification(
            err.message || "Failed to fetch overview data"
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },

    loadDailyRevenue(year, month) {
      this.loading = true;
      let params = {
        year: year,
        month: month,
      };

      this.month = month;
      this.chart_daily = true;

      this.fetchDataByParams("revenue", params)
        .then((res) => {
          this.chart_dtd_data = res.data || [];
        })
        .catch((err) => {
          this.errorNotification(
            err.message || "Failed to fetch daily revenue data"
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onDragDates(e) {
      this.setPopoverInfo(e);
    },

    onInputDates(e) {
      if (!e) {
        return;
      }

      this.dateRange = e;
    },

    setPopoverInfo(e) {
      if (!e) {
        return;
      }

      this.popStartDate = this.$moment(e.start);
      this.popEndDate = this.$moment(e.end);

      this.popDays = this.popEndDate.diff(this.popStartDate, "day") + 1;

      this.popStartDate = this.popStartDate.format("ddd, MMM D, YYYY");
      this.popEndDate = this.popEndDate.format("ddd, MMM D, YYYY");
    },
  },

  watch: {
    getDateRange(dates) {
      this.setPopoverInfo(dates);
      this.loadData();
    },
  },
};
</script>

<style lang="scss" scope>
.overview-container {
  background-color: #f5f7fa;
  padding-top: 10px;
}

.overview-header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  // background-color: #eee;
  padding: 10px 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  position: sticky;
  top: 0;
  z-index: 2;
  min-height: 60px; /* Maintain constant height */
}

.welcome-message {
  font-weight: 600;
}

.overview-card-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 10px;
  position: relative;

  .card {
    flex: 1;
    margin: 0;
    padding: 0px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    border: 1px solid #e9ecef;
    border-radius: 6px;
    max-width: 200px;

    &.bg-occupancy {
      background-color: #ffffff;
      .card-header,
      .card-body h3 {
        color: #2c3e50;
      }
    }

    &.bg-adr {
      background-color: #ffffff;
      .card-header,
      .card-body h3 {
        color: #34495e;
      }
    }

    &.bg-rev-par {
      background-color: #ffffff;
      .card-header,
      .card-body h3 {
        color: #3d566e;
      }
    }

    &.bg-departures {
      background-color: #ffffff;
      .card-header,
      .card-body h3 {
        color: #4b6584;
      }
    }

    &.bg-arrivals {
      background-color: #ffffff;
      .card-header,
      .card-body h3 {
        color: #546e7a;
      }
    }

    &.bg-bookings,
    &.bg-guests {
      background-color: #ffffff;
      .card-header,
      .card-body h3 {
        color: #5d6d7e;
      }
    }
  }

  .card-body {
    position: relative;
    padding-bottom: 50px;

    &.no-padding {
      padding-bottom: 5px;
    }

    h1 {
      position: absolute;
      top: 65px;
      left: 0;
      width: 100%;
    }

    h3 {
      font-size: 22px;
      line-height: 24px;
    }
  }

  .card-footer,
  .card-header {
    background: none;
    padding: 5px;
    text-align: center;
  }

  .card-header {
    font-weight: bold;
    padding: 10px;
  }

  .overview-card-value {
    position: absolute;
    bottom: 0px;
    left: 0;
    width: 100%;
  }

  .overview-card-icon {
    display: block;
    margin: auto;
    text-align: center;
    width: 100%;

    svg {
      width: 100%;
      height: auto;
    }

    &--chart {
      margin-top: -10px;
      width: 80%;
    }

    &--plane {
      svg {
        max-width: 50px;
      }
    }

    &--dollar {
      svg {
        max-width: 70px;
      }
    }

    &--booking {
      svg {
        max-width: 50px;
      }
    }

    &--guest {
      svg {
        max-width: 50px;
      }
    }
  }

  &--revenue {
    .card-body {
      position: relative;
      padding-bottom: 50px;
      h3 {
        position: absolute;
        bottom: 5px;
        left: 0;
        width: 100%;
      }
    }
  }
}

.overview-chart-wrapper {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px 10px;
  margin-bottom: 15px;

  /* Add minimum height to ensure consistent layout while loading */
  min-height: 350px;

  canvas {
    margin-top: 10px;
  }
}

.btn-back {
  cursor: pointer;
  color: #fe8d49 !important;
  font-size: 14px;

  &.cursor-normal {
    cursor: default;
    color: #666 !important;
  }
}

@media (min-width: 992px) {
  .overview-card-wrapper {
    -ms-flex: 0 0 20%;
    -webkit-box-flex: 0;
    flex: 0 0 20%;
    max-width: 20%;
  }
}

/* can this be merged across the files */
.loading-message {
  position: relative;
  top: -70px;
  color: #fcfcfc;
}
.fetching-spinner-content {
  background: transparent !important;
  border: none !important;
}

.chart-placeholder {
  background-color: #f0f0f0;
  border-radius: 4px;
  padding: 50px 20px;
  text-align: center;
  margin: 10px 0;
}

.chart-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 0 10px;
}

.chart-title h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.svg-icon-wrapper {
  display: inline-block;
  background-color: #ccc;
  color: #555;
  width: 40px;
  height: 40px;
  line-height: 40px;
  border-radius: 50%;
  text-align: center;
  font-size: 10px;
}

.dollar-icon {
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: #468a46;
  width: 50px !important;
  height: 50px !important;
  border-radius: 50%;
  font-size: 24px !important;
  font-weight: bold;
  background-color: rgba(255, 255, 255, 0.3) !important;
  margin: 0 auto;
  line-height: 1;
}

.plane-icon {
  display: flex !important;
  align-items: center;
  justify-content: center;
  width: 50px !important;
  height: 50px !important;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3) !important;
  margin: 0 auto;
  line-height: 1;
}

.plane-icon span {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: bold;
  color: #fff;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.2);
}

.booking-icon {
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: #007bff;
  width: 50px !important;
  height: 50px !important;
  border-radius: 50%;
  background-color: rgba(0, 123, 255, 0.15) !important;
  margin: 0 auto;
  line-height: 1;
  i {
    font-size: 28px !important;
  }
}

.guest-icon {
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: #fd7e14;
  width: 50px !important;
  height: 50px !important;
  border-radius: 50%;
  background-color: rgba(253, 126, 20, 0.15) !important;
  margin: 0 auto;
  line-height: 1;
  i {
    font-size: 28px !important;
  }
}

.percent-icon {
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: #2e8b57;
  width: 50px !important;
  height: 50px !important;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3) !important;
  margin: 0 auto;
  line-height: 1;
}

/* new grid styling */
.group-card {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  .metric-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    min-height: 300px; /* Ensures consistent height even with fewer metrics */
  }
  .metric-item {
    flex: 0 0 45%;
    margin-bottom: 15px;
  }
  .metric-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin: 0 auto;
  }
}

.daterange-picker {
  margin-bottom: 0 !important;
  margin-top: 10px !important;
  margin-right: 20px !important;
  margin-left: 20px !important;
}

.popover-content p {
  margin: 0;
}

.days-nights {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 3px;

  .days,
  .nights {
    font-weight: 700;
    display: flex;
    align-items: center;
    &:not(:first-child) {
      margin-left: 13px;
    }
  }

  .vc-sun-o,
  .vc-moon-o {
    margin-right: 5px;
    width: 16px;
    height: 16px;
  }

  .vc-sun-o {
    color: #ffb366;
  }

  .vc-moon-o {
    color: #4d4d64;
  }
}
</style>
